import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DefaultLocale, isLocaleLanguage, LocaleLanguage } from '@app/+authenticated/shared/locale/locale.helper';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { DepartmentTargetAPI } from '@app/reducers/orm/departmentTarget/department-target.api';
import { InsightsService } from '@app/reducers/orm/insights/insights.service';
import { TrackingService } from '@app/services/tracking.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KnowledgeBaseArticleLinkPipe } from '@sb/shared/knowledge-base';
import { ButtonThemePalette } from '@sb/ui/button';
import { DIALOG_DATA, DialogActivationComponent, DialogRef } from '@sb/ui/dialog';
import { InputModule } from '@sb/ui/input';
import { KBArticle, ModalBundle } from '@shiftbase-com/ts-shared/models';
import { finalize } from 'rxjs';

import { OnboardingDialogResult } from '../../../+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { KnowledgeBaseArticleLinkModule } from '../../../pipes/knowledge-base-article-link.module';
import { OnboardingChapterModule } from '../components/chapter.module';
import { TargetSettingSetupComponent } from './setup/target-setting-setup.component';
import { TargetSettingDialogStep } from './target-setting.model';

// english loom video serves as default
const defaultLoomVideoId = 'eea90c9c7e7e42559dd9261ad96baaad?sid=b46b9492-73e8-4515-b516-a7a49ecbdbdd';
const loomVideoMap: Partial<Record<LocaleLanguage, string>> = {
  [LocaleLanguage.DUTCH]: '385640916dd24fd4b8b5280c9a3df7a0?sid=e1c4dc17-5168-4ffc-bca1-803956bf7efb',
  [LocaleLanguage.GERMAN]: '6d4f80b7e7a5416c93c9bcd34d278b83?sid=759ddff4-cd3e-489c-9158-de3e4bd9f284',
};

const getLoomVideoId = (language: string): string =>
  (isLocaleLanguage(language) ? loomVideoMap[language] : undefined) ?? defaultLoomVideoId;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'target-setting-dialog',
  imports: [
    DialogActivationComponent,
    NgStyle,
    TranslatePipe,
    OnboardingChapterModule,
    InputModule,
    KnowledgeBaseArticleLinkModule,
    TargetSettingSetupComponent,
    ReactiveFormsModule,
  ],
  providers: [PlanCheckPipe],
  templateUrl: './target-setting-dialog.component.html',
})
export class TargetSettingDialogComponent implements OnInit {
  eventData = inject<ModalBundle>(DIALOG_DATA);
  private dialogRef = inject<DialogRef<OnboardingDialogResult>>(DialogRef);
  private insightsService = inject(InsightsService);
  private destroyRef = inject(DestroyRef);
  private knowledgeBaseArticleLinkPipe = inject(KnowledgeBaseArticleLinkPipe);
  private departmentTargetApi = inject(DepartmentTargetAPI);
  private translate = inject(TranslateService);
  private sanitizer = inject(DomSanitizer);
  private trackingService = inject(TrackingService);
  private router = inject(Router);

  ButtonThemePalette = ButtonThemePalette;

  targetSettingDialogStep = TargetSettingDialogStep;
  currentStep = TargetSettingDialogStep.TEASER;
  steps = 7;

  wagesSet = false;

  formGroup = new FormGroup(
    {
      productivity: new FormControl<string>(null, [Validators.min(0)]),
      averageHourlyWage: new FormControl<string>(null, [Validators.min(0)]),
      laborCostPercentage: new FormControl<string>(null, [Validators.min(0), Validators.max(100)]),
    },
    [
      (formGroup: FormGroup) => {
        const hasValues = Object.values(formGroup.value).some((value) => !!value);
        return hasValues ? null : { noValues: true };
      },
    ],
  );

  currentYear = new Date().getFullYear();
  eventTracked = false;

  nextButtonLoading = false;

  ngOnInit(): void {
    void this.insightsService
      .getOnboardingInsights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((insights) => {
        this.wagesSet = insights.wagesFilledInPercentage >= 90;
      });
  }

  goBack() {
    this.updateStep(this.currentStep - 1);
  }

  goToStep(step: TargetSettingDialogStep) {
    if (this.currentStep === TargetSettingDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Activation Target Setting Started', this.eventData);
      this.eventTracked = true;
    }
    this.updateStep(step);
  }

  private updateStep(step: TargetSettingDialogStep) {
    this.currentStep = step;
    this.dialogRef.disableClose = step !== this.targetSettingDialogStep.TEASER;
  }

  close() {
    this.dialogRef.close();
    this.router.navigateByUrl('/account/settings/targets');
  }

  savePreferencesAndGoToLastStep() {
    this.nextButtonLoading = true;
    const values = this.formGroup.value;

    void this.departmentTargetApi
      .setOnboardingTargets(values)
      .pipe(
        finalize(() => {
          this.nextButtonLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateStep(this.targetSettingDialogStep.SUMMARY);

        this.trackingService.event('Activation Target Setting Completed', this.eventData);
      });
  }

  redirectToHelpCenter(article: KBArticle) {
    const link = this.knowledgeBaseArticleLinkPipe.transform(article);
    window.open(link, '_blank');
  }

  private loomVideoId: string = getLoomVideoId(this.translate.getCurrentLang() ?? DefaultLocale);

  loomVideoLink: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    `https://www.loom.com/embed/${this.loomVideoId}?hide_share=true&hide_speed=true&hide_title=true&hide_owner=true`,
  );
}
